import React, { useState, useEffect, useMemo, memo, useRef, useCallback } from 'react';
import { styled } from '@compiled/react';

import { Inline, Box, xcss } from '@atlaskit/primitives';
import { N0, N30 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { Breadcrumbs } from '@confluence/breadcrumbs';
import { TeammatePresenceLoader } from '@confluence/experiment-teammate-presence';
import { SaveIndicator } from '@confluence/save-indicator';
import {
	EditorContentClassificationTag,
	useIsDataClassificationEnabledForOrg,
	DataClassificationHoverButton,
	ContentTypeClassification,
	useIsClassificationEnabledForContentTypeExpansion,
} from '@confluence/data-classification';
import { EditorPrimaryToolbarComponentsWrapper } from '@confluence/editor-primary-toolbar-components';
import { RequestToEditLoader } from '@confluence/no-permission/entry-points/RequestToEditLoader';
import { RequestToEditLoaderAfterPaint } from '@confluence/no-permission/entry-points/RequestToEditLoaderAfterPaint';
import { LiveEditLozenge } from '@confluence/live-pages-features/entry-points/LiveEditLozenge';
import { useBooleanFeatureFlag } from '@confluence/session-data';

import type { EditorActionBarProps } from './EditorActionBarProps';

// If any change of css happens that caused EditorActionBar to have a different height,
// Update EDITOR_ACTION_BAR_HEIGHT in EditorDisplayWrapper and EditorHelpCenterComponent and EditorDraftBannerStyles
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EditorActionBarWrapper = styled.div<{
	isHidden: boolean;
	isClassificationsEnabled: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: (props) => (props.isHidden ? 'none' : 'flex'),
	position: 'relative',
	paddingLeft: token('space.300', '24px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingTop: (props) =>
		props.isClassificationsEnabled ? token('space.100', '8px') : token('space.150', '12px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingBottom: (props) =>
		props.isClassificationsEnabled ? token('space.100', '8px') : token('space.150', '12px'),
	justifyContent: 'space-between',
	borderBottomColor: token('color.border', N30),
	borderBottomStyle: 'solid',
	borderBottomWidth: '1px',
	minHeight: '36px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) => (props.isClassificationsEnabled ? '48px' : 'unset'),
	// editor inline coment sidebar has a zIndex of 501, and editor toolbar has a zIndex of 510, set a slighly higher zIndex would fix overlap issue with popups from inline comments and invite to share popup.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
	...(FeatureGates.checkGate('confluence_frontend_editor_popup_cutoff_fix') && {
		zIndex: 520,
		backgroundColor: token('elevation.surface', N0),
	}),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ClassificationTagPlaceholder = styled.div({
	height: '26px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BreadcrumbsAndClassificationTagWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'flex-start',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ComponentsWrapper = styled.div({
	display: 'flex',
	flexBasis: '100%',
	minWidth: 'fit-content',
	justifyContent: 'flex-end',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const VerticalSeparator = styled.div({
	borderLeft: `2px solid ${token('color.border', 'rgba(107, 119, 140, 0.3)')}`,
	display: 'inline-block',
	width: '1px',
	height: '16px',
	margin: `0 ${token('space.075', '6px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.span({
	marginRight: `${token('space.075', '6px')}`,
});

const contentTypeClassificationStyles = xcss({
	paddingTop: 'space.025',
});

// This is the small screen breakpoint from https://atlassian.design/components/primitives/responsive/breakpoints/examples
const EDITOR_ACTION_BAR_HIDE_BREADCRUMB_BREAK_POINT = 768;

const EditorActionBar = ({
	spaceKey,
	editorActions,
	collabEditState,
	title,
	areMainButtonsDisabled,
	forceDisabled,
	userOffline,
	contentId,
	contentType,
	isContentCreationPanelOpen,
	isUnpublishedDraft,
	onClickPublish,
	onClickOpenContentCreationPanel,
	onPreviewClicked,
	saveDraft,
	collabLimitReached,
	showRevertDialog,
	retrieveEditorTitleAndContent,
	isPublishingContent,
	isSingleUserSession,
	navigateOnClose,
	triggerAnalyticsEvent,
	shouldRenderPreview,
	shouldShowRecentSpotlight,
	checkIfConditionPreventingPublish,
	flags,
	createAnalyticsEvent,
	collabEditOption,
	isCustomSitesPageTitleFFOn,
	hasEditPermission,
	editorDisabled,
	isTitleFocused,
	viewMode,
	contentClassificationMetadata,
	showLiveEditLozenge,
	isLivePage,
}: EditorActionBarProps) => {
	const { contentLevelLoading, spaceLevelLoading } = contentClassificationMetadata;

	const isClassificationEnabledForContentTypeExpansion =
		useIsClassificationEnabledForContentTypeExpansion(contentType, isLivePage);

	const isClassificationLevelDisabledForLivePage =
		!isClassificationEnabledForContentTypeExpansion && isLivePage;

	const { isEnabled: isDataClassificationEnabledForOrg } = useIsDataClassificationEnabledForOrg();
	const [hideBreadcrumb, setHideBreadcrumb] = useState(false);
	const isEditorActionBarSSREnabled = useBooleanFeatureFlag(
		'confluence.frontend.ssr-editor-actions-bar',
	);

	const resizeObserver = useMemo(() => {
		if (process.env.REACT_SSR) {
			return null;
		}

		return new ResizeObserver((entries: Array<ResizeObserverEntry>) => {
			for (const entry of entries) {
				if (entry.contentRect.width) {
					if (entry.contentRect.width < EDITOR_ACTION_BAR_HIDE_BREADCRUMB_BREAK_POINT) {
						setHideBreadcrumb(true);
					}
					if (entry.contentRect.width > EDITOR_ACTION_BAR_HIDE_BREADCRUMB_BREAK_POINT + 20) {
						// Unhide breadcrumb with some buffer to avoid glitching
						setHideBreadcrumb(false);
					}
				}
			}
		});
	}, [setHideBreadcrumb]);

	// Hide breadcrumbs when content body width is smaller than the breakpoint
	useEffect(() => {
		// Content body does not include content tree
		const contentBody: HTMLElement | null = document.getElementById('content-body');

		if (!contentBody) {
			return;
		}

		resizeObserver?.observe(contentBody);

		return () => {
			resizeObserver?.unobserve(contentBody);
		};
	}, [resizeObserver]);

	const RequestToEditLoaderComponent = isEditorActionBarSSREnabled
		? RequestToEditLoaderAfterPaint
		: RequestToEditLoader;

	const isCollabNetworkUnreachable =
		(editorDisabled || collabLimitReached) &&
		!isTitleFocused &&
		!collabEditState.collabEditInternetLost &&
		!collabEditState.collabEditConnectionTimedOut;

	const hideSaveIndicator = viewMode || isCollabNetworkUnreachable;

	const { shouldDisplayClassification, hasNoClassification } = contentClassificationMetadata;
	const shouldNotLoadClassificationPlaceholder =
		isLivePage && !hasEditPermission && hasNoClassification;

	return (
		<EditorActionBarWrapper
			data-testid="editor-action-bar"
			isHidden={shouldRenderPreview}
			isClassificationsEnabled={
				!isClassificationLevelDisabledForLivePage && !shouldNotLoadClassificationPlaceholder
			}
		>
			<BreadcrumbsAndClassificationTagWrapper>
				<Inline alignBlock="center">
					{!hideBreadcrumb && (
						<div data-testId="editor-breadcrumbs-container">
							<Breadcrumbs
								contentId={contentId}
								spaceKey={spaceKey}
								contentTitle={title ?? undefined}
							/>
						</div>
					)}
					{showLiveEditLozenge && <LiveEditLozenge showIcon />}
				</Inline>
				{/* Display a placeholder during SSR when user doesn't have edit perms and page does have a data classification */}
				{isEditorActionBarSSREnabled &&
					isDataClassificationEnabledForOrg &&
					process.env.REACT_SSR &&
					!hasEditPermission &&
					!hasNoClassification && <ClassificationTagPlaceholder />}
				{/* Display a placeholder during SSR when user has edit perms */}
				{isEditorActionBarSSREnabled &&
					isDataClassificationEnabledForOrg &&
					process.env.REACT_SSR &&
					hasEditPermission && <ClassificationTagPlaceholder />}
				{/* Display a placeholder for classification to prevent cls */}
				{isCustomSitesPageTitleFFOn &&
					(contentLevelLoading || spaceLevelLoading) &&
					!shouldNotLoadClassificationPlaceholder &&
					!isClassificationLevelDisabledForLivePage && <ClassificationTagPlaceholder />}
				{/* Displays classification level */}
				{isCustomSitesPageTitleFFOn && shouldDisplayClassification && !isLivePage && (
					<div data-testid="content-classification-tag">
						<EditorContentClassificationTag
							contentId={contentId}
							contentType={contentType as string}
							contentClassificationMetadata={contentClassificationMetadata}
							isEditMode
							customSitesTitleComponent
							spaceKey={spaceKey}
							userOffline={userOffline}
						/>
					</div>
				)}
				{/* Displays Add Classification button (in the case where classification shouldn't be displayed & classifications FFs are enabled) */}
				{isCustomSitesPageTitleFFOn &&
					!shouldDisplayClassification &&
					isDataClassificationEnabledForOrg &&
					hasNoClassification &&
					!isLivePage && (
						<DataClassificationHoverButton
							contentId={contentId}
							shouldShowHoverButton
							userOffline={userOffline}
							isCustomSitesPageTitleFFOn={isCustomSitesPageTitleFFOn}
							contentClassificationMetadata={contentClassificationMetadata}
							hasClassification={shouldDisplayClassification}
							spaceKey={spaceKey}
						/>
					)}
				{/* Displays classification level or add button, for live pages */}
				{/* When in SSR, do not render <ContentTypeClassification /> b/c the styles are broken when in SSR mode for live pages.
				The check for !process.env.REACT_SSR can be removed when data classification styles are loaded correctly in SSR mode */}
				{isLivePage && isClassificationEnabledForContentTypeExpansion && !process.env.REACT_SSR && (
					<Box xcss={contentTypeClassificationStyles}>
						<ContentTypeClassification
							spaceKey={spaceKey}
							contentId={contentId}
							contentType={contentType}
							contentClassificationMetadata={contentClassificationMetadata}
							readOnly={!hasEditPermission}
						/>
					</Box>
				)}
			</BreadcrumbsAndClassificationTagWrapper>
			<ComponentsWrapper>
				{!hideSaveIndicator && (
					<SaveIndicator
						editorActions={editorActions}
						saveError={
							!!collabEditState.collabEditError ||
							collabEditState.collabEditInternetLost ||
							collabEditState.collabEditConnectionTimedOut
						}
						title={title}
					/>
				)}
				<TeammatePresenceLoader
					collabEditOption={collabEditOption}
					contentId={contentId}
					contentType={contentType}
					spaceKey={spaceKey}
					isEditorActionBarSSREnabled={isEditorActionBarSSREnabled}
				/>
				<VerticalSeparator />
				{!hasEditPermission && (
					<ButtonWrapper>
						<RequestToEditLoaderComponent contentId={contentId} />
					</ButtonWrapper>
				)}
				<EditorPrimaryToolbarComponentsWrapper
					areMainButtonsDisabled={areMainButtonsDisabled}
					forceDisabled={forceDisabled}
					userOffline={userOffline}
					contentId={contentId}
					contentType={contentType}
					isContentCreationPanelOpen={isContentCreationPanelOpen}
					isUnpublishedDraft={isUnpublishedDraft}
					onClickPublish={onClickPublish}
					onClickOpenContentCreationPanel={onClickOpenContentCreationPanel}
					onPreviewClicked={onPreviewClicked}
					saveDraft={saveDraft}
					spaceKey={spaceKey}
					title={title}
					collabLimitReached={collabLimitReached}
					showRevertDialog={showRevertDialog}
					retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
					isPublishingContent={isPublishingContent}
					editorActions={editorActions}
					isSingleUserSession={isSingleUserSession}
					navigateOnClose={navigateOnClose}
					triggerAnalyticsEvent={triggerAnalyticsEvent}
					shouldRenderPreview={shouldRenderPreview}
					shouldShowRecentSpotlight={shouldShowRecentSpotlight}
					checkIfConditionPreventingPublish={checkIfConditionPreventingPublish}
					flags={flags}
					createAnalyticsEvent={createAnalyticsEvent}
					viewMode={viewMode}
				/>
			</ComponentsWrapper>
		</EditorActionBarWrapper>
	);
};

// Returns a stable version of the given function.
// The value (function reference) returned on the first call would always be the same,
// but the called function would always be the latest version passed as the argument.
// In a sense, it's a version of useCallback, which allows to not care about the dependencies.
export function useStableFunc<T extends (...args: any[]) => any>(func: T): T {
	const ref = useRef<T>();
	ref.current = func;
	const stableFunc = useCallback((...args: any) => ref.current?.(...args), []);
	return stableFunc as unknown as T;
}

const MemoEditorActionBar = memo(EditorActionBar);

const MemoizedEditorActionBar = (props: EditorActionBarProps) => {
	const showRevertDialog = useStableFunc(props.showRevertDialog);
	const onClickPublish = useStableFunc(props.onClickPublish);
	return (
		<MemoEditorActionBar
			{...props}
			showRevertDialog={showRevertDialog}
			onClickPublish={onClickPublish}
		/>
	);
};

export { MemoizedEditorActionBar as EditorActionBar };
